import { useState, useEffect, useRef } from "react";
import {
  Card,
  InputNumber,
  Select,
  AutoComplete,
  Radio,
  Typography,
  Divider,
  Slider,
  Spin,
  Button,
  Grid,
} from "antd";
import { motion, AnimatePresence } from "framer-motion";
import DetalleCredito from "./DetalleCredito";
import ModalSinCobertura from "./ModalSinCobertura";

import getDepartments from "../helpers/getDepartments";
import getMunicipios from "../helpers/getMunicipios";
import calculateCuote from "../helpers/calculateCuote";
import getParameters from "../helpers/getParameters";
import ModalInfo from "./ModalInfo";
import { useNotification } from "./NotificationContext";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const fadeIn = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const SimuladorCredito = () => {
  const [departamento, setDepartamento] = useState();
  const [municipios, setMunicipios] = useState([]);
  const [municipio, setMunicipio] = useState();
  const [monto, setMonto] = useState();
  const [cuotas, setCuotas] = useState();
  const [periodicidad, setPeriodicidad] = useState();
  const [sexo, setSexo] = useState();
  const [currentStep, setCurrentStep] = useState(1);
  const [modalSinCobertura, setModalSinCobertura] = useState(false);
  const [departamentoSinCobertura, setDepartamentoSinCobertura] = useState("");
  const [monthOptions, setMonthOptions] = useState([]);

  const [departamentos, setDepartamentos] = useState([]);
  const [parameters, setParameters] = useState(null);
  const [cuotaResponse, setCuotaResponse] = useState(null);

  const [loadingParameters, setLoadingParameters] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [calculatingCuota, setCalculatingCuota] = useState(false);
  const [showBotonSimular, setShowBotonSimular] = useState(false);
  const [pagosPorAnio, setPagosPorAnio] = useState();
  // Estados para datos seleccionados completos
  const [selectedMunicipio, setSelectedMunicipio] = useState(null);

  const detalleCreditoRef = useRef(null);

  const screens = useBreakpoint();
  const { notify } = useNotification();

  // Cargar departamentos al montar el componente
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);

        const deps = await getDepartments();
        setDepartamentos(deps);
      } catch (error) {
        console.error("Error al cargar departamentos:", error);
        notify(
          "¡Oops! Tuvimos un problema al cargar los departamentos. Intenta nuevamente"
        );
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, [notify]);

  useEffect(() => {
    if (cuotaResponse?.cuotaAproximada && detalleCreditoRef.current) {
      detalleCreditoRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [cuotaResponse]);

  const handleDepartamentos = async (value) => {
    await setCuotaResponse(null);

    if (!value) {
      await setDepartamento("");
      await setMunicipio("");
      await setSelectedMunicipio(null);
      await await setMunicipios([]);
      await setPeriodicidad(null);
      await setParameters(null);
      await setCuotas(null);
      return;
    }

    const deptoSeleccionado = departamentos.find((d) => d.name === value);

    if (!deptoSeleccionado) {
      setDepartamento(value);
      setMunicipio("");
      setSelectedMunicipio(null);
      setMunicipios([]);
      setPeriodicidad(null);
      return;
    }

    setDepartamento(deptoSeleccionado.name);

    setMunicipio("");
    setSelectedMunicipio(null);
    setMunicipios([]);

    if (!deptoSeleccionado.presence) {
      setDepartamentoSinCobertura(value);
      setModalSinCobertura(true);
      setDepartamento("");
      return;
    }

    try {
      setLoadingMunicipios(true);
      const municipiosData = await getMunicipios(deptoSeleccionado.code);
      setMunicipios(municipiosData);
    } catch (error) {
      console.error("Error al cargar municipios:", error);
      notify(
        "¡Oops! Tuvimos un problema al cargar los municipios. Intenta nuevamente"
      );
      setMunicipios([]);
    } finally {
      setLoadingMunicipios(false);
    }
  };

  const handleMunicipio = async (value) => {
    await await setCuotaResponse(null);

    if (!value) {
      await setMunicipio("");
      await setSelectedMunicipio(null);
      await setParameters(null);
      await setPeriodicidad(null);
      await setCuotas(null);
      await setPeriodicidad(null);
      return;
    }

    const municipioSeleccionado = municipios.find((m) => m.name === value);

    if (!municipioSeleccionado) {
      setMunicipio(value);
      setSelectedMunicipio(null);
      setParameters(null);
      return;
    }

    if (!municipioSeleccionado.presence) {
      setDepartamentoSinCobertura(value);
      setModalSinCobertura(true);
      setMunicipio("");
      setSelectedMunicipio(null);
      setParameters(null);
      return;
    }

    setMunicipio(value);
    setSelectedMunicipio(municipioSeleccionado);

    try {
      setLoadingParameters(true);

      const response = await getParameters(
        `?gender=${sexo}&tipoMunicipio=${municipioSeleccionado.clasification}`
      );

      const params = response?.[0] || null;
      setParameters(params);

      if (params?.montoMinimo) {
        setMonto(params.montoMinimo);
      }
    } catch (error) {
      console.error("Error al manejar municipio:", error);
      notify(
        "¡Oops! Tuvimos un problema al consultar algunos datos. Por favor intenta nuevamente mas tarde."
      );
      setParameters(null);
    } finally {
      setLoadingParameters(false);
    }
  };

  useEffect(() => {
    if (sexo && currentStep === 1) setCurrentStep(2);
    if (departamento && municipio && currentStep === 2) setCurrentStep(3);
    if (monto && currentStep === 3) setCurrentStep(4);
    if (periodicidad && currentStep === 4) setCurrentStep(5);
    if (cuotas && currentStep === 5) setCurrentStep(6);
  }, [sexo, monto, departamento, municipio, cuotas, periodicidad, currentStep]);

  // Calcular cuota cuando todos los datos estén completos
  useEffect(() => {
    const mostrarBoton = async () => {
      if (
        !departamento ||
        !municipio ||
        !monto ||
        !cuotas ||
        !periodicidad ||
        !sexo ||
        !selectedMunicipio
      ) {
        setCuotaResponse(null);
        setShowBotonSimular(false);
        return;
      }
      setShowBotonSimular(true);
    };

    mostrarBoton();
  }, [
    departamento,
    municipio,
    monto,
    cuotas,
    periodicidad,
    sexo,
    selectedMunicipio,
  ]);

  // Mostrar loading mientras cargan los parámetros iniciales
  if (loadingParameters) {
    return (
      <Card
        style={{
          maxWidth: screens.lg ? "50%" : screens.md ? "60%" : 400,
          margin: "auto",
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(2, 20, 66, 0.1)",
          textAlign: "center",
          padding: screens.lg ? 60 : screens.md ? 40 : 10,
        }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Cargando ...</Text>
        </div>
      </Card>
    );
  }

  const handleChange = async (value, option) => {
    setPeriodicidad(value);
    setPagosPorAnio(option.pagosPorAnio);
    const monthOption = [];
    await option.monthOption.forEach((option) => {
      if (
        option >= parameters.plazoMinimo &&
        option <= parameters.plazoMaximo
      ) {
        monthOption.push(option);
      }
    });
    setMonthOptions(monthOption);
    setCuotas(null);
    setCuotaResponse(null);
  };

  const calculate = async () => {
    try {
      setCalculatingCuota(true);

      const request = {
        genero: sexo,
        monto: monto,
        codigoMunicipio: selectedMunicipio.Código || selectedMunicipio.code,
        municipioRural: selectedMunicipio.clasification === "Rural",
        nombreMunicipio: selectedMunicipio.name,
        nombreDepartamento: departamento,
        periodicidad: periodicidad,
        plazo: cuotas,
      };

      const response = await calculateCuote(request);
      const cargos = Array.isArray(response.cargos) ? response.cargos : [];
      const getValue = (desc) =>
        cargos.find((c) => c.description === desc)?.value ?? 0;

      const respuesta = {
        cuotaAproximada: response.cuota,
        tasaInteres: response.tasaMV,
        tasaEfectivaEA: response.tasaEfectivaEA,
        cargosCuota: response.cargosCuota,
        cuotas: response.cuotas,
        evaluacionCredito: getValue("Evaluación del crédito"),
        garantia: getValue("Garantía"),
        seguroDeudores: getValue("Seguro de deudores"),
        valorDesembolsar: response.desembolso,
        cargos: response.cargos,
      };

      setCuotaResponse(respuesta);
    } catch (error) {
      console.error("Error al calcular cuota:", error);
      notify(
        "Ooops, tuvimos problemas al calcular la cuota. Por favor intenta nuevamente"
      );
      setCuotaResponse(null);
    } finally {
      setCalculatingCuota(false);
    }
  };

  const onChageSexo = (value) => {
    setDepartamento([]);
    setMunicipio([]);
    setPeriodicidad(null);
    setCuotaResponse(null);
    setSexo(value);
  };

  const onChangeMonto = (value) => {
    setCuotaResponse(null);
    setMonto(value);
  };

  const onChangeCuotas = (value) => {
    setCuotas(value);
    setCuotaResponse(null);
  };

  const messageSelection = () => {
    return showBotonSimular ? (
      <p style={{ color: "#021442", textAlign: "center" }}>
        {`Elegiste pagar `}
        <strong>{periodicidad?.toLowerCase()}</strong>
        {`, con un plazo total de `}
        <strong>{cuotas}</strong>
        {` meses. Tendrás `}
        <strong>{cuotas / (12 / pagosPorAnio)}</strong>
        {` cuota${
          cuotas / (12 / pagosPorAnio) > 1 ? "s " : " "
        }en total para completar tu pago.`}
      </p>
    ) : (
      <></>
    );
  };

  return (
    <>
      <Card
        style={{
          maxWidth: screens.lg ? "50%" : screens.md ? "60%" : 400,
          margin: "auto",
          borderRadius: 16,
          boxShadow: "0 4px 12px rgba(2, 20, 66, 0.1)",
          textAlign: "center",
          padding: screens.lg ? 60 : screens.md ? 40 : 10,
        }}
      >
        <AnimatePresence mode="wait">
          {currentStep >= 1 && (
            <motion.div
              key="sexo"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={fadeIn}
            >
              <Title
                level={3}
                style={{ color: "#021442", textAlign: "center" }}
              >
                ¿Cuál es tu sexo?
              </Title>
              <Radio.Group
                value={sexo}
                size="large"
                onChange={(e) => onChageSexo(e.target.value)}
                buttonStyle="solid"
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Radio.Button
                  value="Mujer"
                  style={{ flex: 1, textAlign: "center" }}
                >
                  Mujer
                </Radio.Button>
                <Radio.Button
                  value="Hombre"
                  style={{ flex: 1, textAlign: "center" }}
                >
                  Hombre
                </Radio.Button>
                <Radio.Button
                  value="Otro"
                  style={{ flex: 1, textAlign: "center" }}
                >
                  Otro
                </Radio.Button>
              </Radio.Group>
              <Divider variant="solid" />
            </motion.div>
          )}

          {currentStep >= 2 && (
            <motion.div
              key="ubicacion"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={fadeIn}
            >
              <Title
                level={3}
                style={{ color: "#021442", textAlign: "center" }}
              >
                ¿Dónde está ubicado tu negocio?
              </Title>

              {/* DEPARTAMENTOS */}
              <AutoComplete
                value={departamento}
                onChange={(value) => {
                  handleDepartamentos(value);
                }}
                onSelect={handleDepartamentos}
                size="large"
                options={departamentos.map((d) => ({
                  value: d.name, // lo que se envía al seleccionar
                  label: <span className="notranslate">{d.name}</span>, // lo que se muestra
                }))}
                placeholder="Selecciona el Departamento"
                style={{
                  width: "100%",
                  marginBottom: 16,
                }}
                translate="no"
                className="notranslate custom-autocomplete"
                inputStyle={{ textAlign: "center" }}
                disabled={loadingDepartments}
                filterOption={(inputValue, option) =>
                  option?.value
                    ?.toLowerCase()
                    .includes(inputValue?.toLowerCase())
                }
                allowClear
                notFoundContent={
                  <Text type="secondary">No hay resultados</Text>
                }
              />

              {/* MUNICIPIOS */}
              <Spin spinning={loadingMunicipios}>
                <AutoComplete
                  value={municipio}
                  onChange={handleMunicipio}
                  onSelect={(_, option) => handleMunicipio(option.value)}
                  size="large"
                  options={municipios.map((d) => ({
                    value: d.name, 
                    label: <span className="notranslate">{d.name}</span>, 
                  }))}
                  placeholder="Selecciona el Municipio"
                  translate="no"
                  className="notranslate custom-autocomplete"
                  style={{
                    width: "100%",
                    marginBottom: 16,
                  }}
                  inputStyle={{ textAlign: "center" }}
                  disabled={!departamento || loadingMunicipios}
                  filterOption={(inputValue, option) =>
                    option?.value
                      ?.toLowerCase()
                      .includes(inputValue?.toLowerCase())
                  }
                  allowClear
                />
              </Spin>

              <Divider variant="solid" />
            </motion.div>
          )}

          {currentStep >= 3 && parameters && (
            <motion.div
              key="monto"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={fadeIn}
            >
              <Title
                level={3}
                style={{ color: "#021442", textAlign: "center" }}
              >
                ¿Cuánto dinero necesitas?
              </Title>
              <p style={{ color: "#021442", textAlign: "center" }}>
                Escribe o selecciona la cantidad de dinero que necesites
              </p>

              <InputNumber
                value={monto}
                onChange={onChangeMonto}
                placeholder="$ Monto"
                min={parameters.montoMinimo}
                max={parameters.montoMaximo}
                step={50000}
                formatter={(value) =>
                  value
                    ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                    : ""
                }
                parser={(value) => value.replace(/\$\s?|(\.)/g, "")}
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 22,
                }}
                controls={false}
                size="large"
              />

              <Slider
                min={parameters.montoMinimo}
                max={parameters.montoMaximo}
                step={100000}
                tooltip={{
                  formatter: (val) => `$ ${val.toLocaleString("es-CO")}`,
                }}
                value={monto || parameters.montoMinimo}
                onChange={onChangeMonto}
                trackStyle={{ backgroundColor: "#ea4e2f" }}
                handleStyle={{
                  color: "#ea4e2f",
                  backgroundColor: "#ea4e2f",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 4,
                  padding: "0 2px",
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    color: "#ea4e2f",
                    fontWeight: 500,
                  }}
                >
                  {`$ ${parameters.montoMinimo.toLocaleString("es-CO")}`}
                </span>

                <span
                  style={{
                    fontSize: 14,
                    color: "#ea4e2f",
                    fontWeight: 500,
                  }}
                >
                  {`$ ${parameters.montoMaximo.toLocaleString("es-CO")}`}
                </span>
              </div>

              <Divider />
            </motion.div>
          )}

          {currentStep >= 4 && (
            <motion.div
              key="periodicidad"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={fadeIn}
            >
              <Title
                level={3}
                style={{ color: "#021442", textAlign: "center" }}
                translate="no"
                className="notranslate"
              >
                ¿Cada cuánto quieres pagar tu cuota?
              </Title>
              <p
                style={{ color: "#021442", textAlign: "center", fontSize: 14 }}
              >
                *Es la frecuencia con la que debes hacer los pagos. (Ej:
                mensual, bimestral, semestral).
              </p>
              <Select
                value={periodicidad}
                size="large"
                onChange={handleChange}
                placeholder="Selecciona la periodicidad"
                style={{ width: "100%", borderRadius: 8 }}
                translate="no"
                className="notranslate custom-autocomplete"
                options={[
                  {
                    value: "MENSUAL",
                    label: "MENSUAL",
                    monthOption: [3, 6, 12, 18, 24, 36, 48, 60, 72],
                    pagosPorAnio: 12,
                  },
                  {
                    value: "BIMESTRAL",
                    label: "BIMESTRAL",
                    monthOption: [6, 12, 18, 24, 36, 48, 60, 72],
                    pagosPorAnio: 6,
                  },
                  {
                    value: "TRIMESTRAL",
                    label: "TRIMESTRAL",
                    monthOption: [3, 6, 12, 18, 24, 36, 48, 60, 72],
                    pagosPorAnio: 4,
                  },
                  {
                    value: "SEMESTRAL",
                    label: "SEMESTRAL",
                    monthOption: [6, 12, 18, 24, 36, 48, 60, 72],
                    pagosPorAnio: 2,
                  },
                  {
                    value: "ANUAL",
                    label: "ANUAL",
                    monthOption: [12, 24, 36, 48, 60, 72],
                    pagosPorAnio: 1,
                  },
                ]}
              />
              <Divider variant="solid" />
            </motion.div>
          )}

          {currentStep >= 5 && parameters && (
            <motion.div
              key="cuotas"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={fadeIn}
            >
              <Title
                level={3}
                style={{ color: "#021442", textAlign: "center" }}
              >
                ¿En cuántos meses quieres pagar?
              </Title>
              <Radio.Group
                value={cuotas}
                size="large"
                onChange={(e) => onChangeCuotas(e.target.value)}
                buttonStyle="solid"
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                {monthOptions.map((month) => (
                  <Radio.Button
                    key={month}
                    value={month}
                    style={{ flex: 1, textAlign: "center" }}
                  >
                    {month}
                  </Radio.Button>
                ))}
              </Radio.Group>
              <Divider variant="solid" />
            </motion.div>
          )}
          {showBotonSimular && messageSelection()}

          <Button
            type="primary"
            disabled={!showBotonSimular}
            style={
              showBotonSimular
                ? {
                    backgroundColor: "#ea4e2f",
                    borderRadius: "24px",
                    fontWeight: "600",
                    height: "48px",
                  }
                : {
                    backgroundColor: "#f0f0f0",
                    color: "#666",
                    border: "none",
                    borderRadius: "24px",
                    fontWeight: "600",
                    height: "48px",
                  }
            }
            onClick={() => calculate()}
            block
          >
            Simular
          </Button>
        </AnimatePresence>
      </Card>
      <div ref={detalleCreditoRef}>
        {cuotaResponse?.cuotaAproximada && (
          <DetalleCredito
            cuotaPagar={cuotaResponse.cuotaAproximada}
            cantidadCuotas={cuotaResponse.cuotas}
            periodicidad={periodicidad}
            tasaInteres={cuotaResponse.tasaInteres}
            tasaEfectivaEA={cuotaResponse.tasaEfectivaEA}
            cargosCuota={cuotaResponse.cargosCuota}
            valorDesembolsar={cuotaResponse.valorDesembolsar}
            monto={monto}
            cargos={cuotaResponse.cargos}
            screens={screens}
          />
        )}
      </div>

      <ModalSinCobertura
        open={modalSinCobertura}
        departamento={departamentoSinCobertura}
        title="¡Lo sentimos!"
        onClose={() => setModalSinCobertura(false)}
      />

      <ModalInfo
        open={calculatingCuota}
        type="info"
        title="Calculando cuota"
        mensaje="Estamos calculando la cuota con tus indicaciones."
        loading={true}
      />
    </>
  );
};

export default SimuladorCredito;
